import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import type { PartnerInfo } from "@gooner/shared";
import { IconClose } from "../control/ui/icons";
import { trackPartnerLink, trackPartnerShown, type PartnerSource } from "../lib/partners";

/**
 * A partner's credit: who gave us the thing you just used, and where to find them.
 *
 * THE ONE RULE THIS COMPONENT KEEPS is that it opens on a deliberate click and in no
 * other way. No timer, no impression, no auto-open — that is the whole difference between
 * a credit and an ad, and it is why this is allowed on a screen at all when AdSlot is not
 * (see its docstring). It lives in `components/` rather than `display/` for the same
 * reason it is written to be general: the channel row is simply its first caller.
 *
 * Cut to AuthModal's skeleton — scrim, portal, header, × — deliberately, because this repo
 * copies dialog bones and cites the source rather than extracting a primitive that would
 * have to accommodate six different ones. Where it goes further than its siblings is the
 * things they are all missing, so the eventual extraction can be cut from here:
 * `aria-modal`, a real `aria-labelledby`, focus restore, and a scrim guard that survives a
 * drag which starts inside the panel and releases outside it.
 *
 * Deliberately NOT a focus trap. That needs a tab-cycling keydown handler and is the kind
 * of thing that half-works; saying so is better than shipping the half.
 *
 * SAFE MODE BLURS THE IMAGERY, and that is the agreed behaviour: the name, tagline, blurb
 * and links are text, which the blur rule does not touch, so the credit survives while the
 * picture does not. Nothing here is exempt.
 */
export function PartnerDialog({
  partner,
  source,
  onClose,
}: {
  partner: PartnerInfo;
  /** Which surface opened this. Travels with both events, so the funnel can say WHICH
   *  placement converted once there is more than one. */
  source: PartnerSource;
  onClose: () => void;
}) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const returnTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Focus restore: the thing that opened this is a control in the bar, and a keyboard
    // user has to get back to it. Captured before we move focus, restored on the way out.
    returnTo.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    // MOUNTING IS THE IMPRESSION, because mounting only happens on a deliberate press —
    // the auth and gold dialogs count themselves the same way.
    trackPartnerShown(partner, source);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      returnTo.current?.focus?.();
    };
  }, [onClose, partner, source]);

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      // target === currentTarget rather than a bare onClose: a drag that starts on the
      // panel and releases out here must not count as clicking the scrim.
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-2xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-6 shadow-2xl"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-zinc-500">
              Partner information
            </p>
            <h2 id={titleId} className="truncate text-lg font-semibold text-zinc-100">
              {partner.name}
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded-full bg-white/10 p-2 text-zinc-300 transition-colors hover:bg-white/20"
          >
            <IconClose width={15} height={15} />
          </button>
        </div>

        {/* THE MARK AT ITS OWN ASPECT, never a forced crop. This slot began square on the
            theory that partner marks usually are — then the first real partner supplied a
            wide banner with a face at each end and the name in the middle, exactly the
            composition a square crop reduces to lettering. Full card width, no aspect
            class: the partner decides the shape, the card only rounds its corners. */}
        <img
          src={partner.imageUrl}
          alt=""
          draggable={false}
          className="mb-4 w-full rounded-xl"
        />

        {partner.tagline ? (
          <p className="mb-4 rounded-lg border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/10 px-3 py-2 text-sm text-[var(--color-accent-soft)]">
            {partner.tagline}
          </p>
        ) : null}

        <p className="mb-4 text-sm leading-relaxed text-zinc-300">{partner.blurb}</p>

        <div className="flex flex-col gap-2">
          {partner.links.map((l) => (
            <a
              key={l.url}
              href={l.url}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackPartnerLink(partner, l.kind)}
              className="rounded-lg bg-[var(--color-accent)] px-4 py-2 text-center text-sm font-semibold text-black transition-colors hover:bg-[var(--color-accent-soft)]"
            >
              {l.label}
            </a>
          ))}
        </div>

        <p className="mt-4 text-[11px] leading-snug text-zinc-500">
          This channel's music is provided by {partner.name}. Opening this card is the only
          way it ever appears.
        </p>
      </div>
    </div>,
    document.body,
  );
}
